import { useCallback, useEffect, useRef, useState } from 'react';

// Shared "load the next page when the user scrolls near the bottom"
// wiring for ChannelsListScreen/TemplatesListScreen/MessagesScreen - an
// IntersectionObserver on a sentinel element rather than a scroll-event
// listener, so it works regardless of which ancestor actually scrolls
// (the window for the two list screens, the message pane's own
// overflow-y container for MessagesScreen).
//
// Guards against firing `onLoadMore` again while a previous call is
// still in flight (IntersectionObserver can report the sentinel as
// intersecting more than once before state/hasMore has had a chance to
// update) and against firing once hasMore is false.
//
// Re-observes the sentinel after every load instead of just leaving the
// same subscription running: IntersectionObserver only invokes its
// callback on a visibility *transition* (not-intersecting -> intersecting
// or back), never just because content changed while it stays visible.
// A page whose content can render far shorter than the viewport (message
// bunching collapsing dozens of bubbles into one small stack, or simply a
// list with only a handful of items) leaves the sentinel visible from the
// very first render and never crossing that boundary again - so without
// this, exactly one page ever loads automatically, then it silently stops
// forever even though hasMore stays true and nothing the user does
// (scrolling included, since there may be nothing to scroll at all)
// resumes it. unobserve+observe forces a fresh intersection check, which
// fires the callback again if the sentinel is *still* visible, so loading
// continues page after page until either the real end of the list is
// reached (hasMore false) or the content finally grows past the viewport
// and the sentinel scrolls out of view.
export function useInfiniteScroll(onLoadMore: () => void | Promise<void>, hasMore: boolean) {
  // A callback ref (backed by state, not a plain useRef) so the observer
  // effect below reruns as soon as the sentinel <div> actually mounts.
  // The caller only renders that div once data has loaded (e.g.
  // `channels.length > 0 && channelsHasMore`), which happens *after* the
  // first mount - a plain ref would leave `sentinelRef.current` null
  // during that initial effect run and, since `hasMore` starts and stays
  // `true` across the transition, the effect would never fire again to
  // pick up the now-mounted element. Net effect: the observer never
  // attached and later pages silently never loaded.
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((el: HTMLDivElement | null) => setSentinelEl(el), []);
  const loadingRef = useRef(false);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!hasMore || !sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || loadingRef.current) return;
        loadingRef.current = true;
        Promise.resolve(onLoadMoreRef.current()).finally(() => {
          loadingRef.current = false;
          observer.unobserve(sentinelEl);
          observer.observe(sentinelEl);
        });
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [hasMore, sentinelEl]);

  return sentinelRef;
}
